import { useEffect, useMemo, useRef, useState } from "react";
import { Pencil, Save, Undo2, X } from "lucide-react";
import type { EditableLayerSummary, GeoJsonFeatureCollection } from "@/lib/types";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Banner, Button, Spinner } from "./ui";
import { useLanguage } from "@/i18n";
import { formatNumber } from "@/lib/format";

/** Edited cells, keyed feature index -> property key -> new text. */
export type AttributeEdits = Record<number, Record<string, string>>;

/**
 * The attribute table for one editable layer -- every feature's `properties`,
 * spreadsheet-style, one row per feature, and editable in place.
 *
 * Columns are the union of every property key across every feature, not just
 * the first one: an uploaded file is under no obligation to have identical
 * keys on every row (a "Mixed" layer especially), and a column silently
 * missing from the table because feature #1 happened not to have it would be
 * a worse failure than an occasional empty cell.
 *
 * **Attributes only -- never geometry.** The shapes themselves are corrected
 * in the field app by whoever is standing at the thing being mapped; this
 * edits what they are tagged with, which is office work and needs a keyboard.
 * Saving sends the whole collection back (the endpoint takes nothing smaller),
 * so geometry rides along untouched exactly as it was read.
 */
export default function EditableLayerTableDialog({
  layer,
  collection,
  loading,
  saving = false,
  error = null,
  onOpenChange,
  onSave,
}: {
  /** Null closes the dialog. */
  layer: EditableLayerSummary | null;
  /** Undefined while still being fetched -- see `loading`. */
  collection: GeoJsonFeatureCollection | undefined;
  loading: boolean;
  saving?: boolean;
  error?: string | null;
  onOpenChange: (open: boolean) => void;
  /**
   * Hands back the edited values; the caller owns the actual write.
   *
   * `close` is false for a single-row save, which has to leave the table open
   * on the rows still being worked through.
   */
  onSave: (edits: AttributeEdits, close: boolean) => Promise<void>;
}) {
  const { t, lang } = useLanguage();
  const features = collection?.features ?? [];
  const [edits, setEdits] = useState<AttributeEdits>({});
  /** Cells are read-only until this is on -- see the same field in
   *  FeatureTable, which this dialog otherwise mirrors. */
  const [editMode, setEditMode] = useState(false);
  /** The row whose own Save is in flight, so only that row shows a spinner. */
  const [savingRow, setSavingRow] = useState<number | null>(null);
  /**
   * Paged, like FeatureTable. Rendering every row at once put 26k rows (times
   * every column -- and an <input> per cell in edit mode) into the DOM, which
   * froze the browser on a municipal layer and made each keystroke re-render
   * all of them.
   */
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);

  /** Set just before a write this dialog started, so the reset below can tell
   *  our own save from a genuine refetch. Cleared as soon as it is read. */
  const selfSaveRef = useRef(false);
  /** What `features.length` was last time the reset ran. */
  const featureCountRef = useRef(features.length);

  // A different layer -- or a fresh fetch of this one -- invalidates anything
  // typed against the old rows, since index N is a different feature now.
  // Also drops back to read-only, so reopening the dialog for a different
  // layer never lands already in edit mode.
  //
  // A save WE made is the exception. It replaces `collection` with the
  // server's copy, which would otherwise wipe every still-pending row and drop
  // out of edit mode the moment one polygon was saved -- exactly the edits the
  // per-row button exists to leave alone. Only skipped while the row count is
  // unchanged: the backend drops features it cannot parse (see _normalise) and
  // renumbers what is left, and a stale index then points at a different
  // polygon, so a shorter collection still resets.
  useEffect(() => {
    const wasSelfSave = selfSaveRef.current;
    selfSaveRef.current = false;
    const sameShape = features.length === featureCountRef.current;
    featureCountRef.current = features.length;
    if (wasSelfSave && sameShape) return;
    setEdits({});
    setEditMode(false);
    setPage(1);
  }, [layer?.id, collection, features.length]);

  const pageCount = Math.max(1, Math.ceil(features.length / pageSize));
  const currentPage = Math.min(page, pageCount);
  const firstRow = (currentPage - 1) * pageSize;
  // A new page starts at its first row, not wherever the last one was scrolled.
  const scrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0 });
  }, [currentPage, pageSize]);
  const pageRows = useMemo(
    () => features.slice(firstRow, firstRow + pageSize).map((_, k) => firstRow + k),
    [features, firstRow, pageSize]
  );

  /**
   * Writes one row's pending edits, leaving every other row's alone.
   *
   * Still a whole-collection PUT underneath -- the endpoint takes nothing
   * smaller -- so this is about which edits are committed, not about sending
   * less. On a large layer that means the full file goes up per row, which is
   * why the all-rows Save beside it stays the cheaper way to write several.
   */
  async function saveRow(index: number) {
    const row = edits[index];
    if (!row || savingRow !== null || saving) return;
    setSavingRow(index);
    selfSaveRef.current = true;
    try {
      await onSave({ [index]: row }, false);
      setEdits((prev) => {
        const out = { ...prev };
        delete out[index];
        return out;
      });
    } catch {
      // onSave's caller surfaces the reason in the dialog's error banner, and
      // the row's edits stay put so the retry is one click.
      selfSaveRef.current = false;
    } finally {
      setSavingRow(null);
    }
  }

  const columns = useMemo(() => {
    const seen = new Set<string>();
    const ordered: string[] = [];
    for (const f of features) {
      for (const key of Object.keys(f.properties ?? {})) {
        if (!seen.has(key)) {
          seen.add(key);
          ordered.push(key);
        }
      }
    }
    return ordered;
  }, [features]);

  const changedRows = Object.keys(edits).length;

  function originalText(index: number, col: string): string {
    const raw = features[index]?.properties?.[col];
    if (raw === null || raw === undefined) return "";
    return typeof raw === "object" ? JSON.stringify(raw) : String(raw);
  }

  function cellValue(index: number, col: string): string {
    const edited = edits[index]?.[col];
    return edited !== undefined ? edited : originalText(index, col);
  }

  function isEdited(index: number, col: string): boolean {
    return edits[index]?.[col] !== undefined;
  }

  function setCell(index: number, col: string, next: string) {
    setEdits((prev) => {
      const row = { ...(prev[index] ?? {}) };
      if (next === originalText(index, col)) {
        // Typed back to what it was: stop counting it as a change, and drop
        // the row once nothing in it differs. Otherwise "3 rows edited" could
        // mean three cells all identical to what the file already holds.
        delete row[col];
      } else {
        row[col] = next;
      }

      const out = { ...prev };
      if (Object.keys(row).length === 0) delete out[index];
      else out[index] = row;
      return out;
    });
  }

  return (
    <Dialog open={layer !== null} onOpenChange={(open) => !saving && onOpenChange(open)}>
      <DialogContent className="max-w-4xl w-[92vw] max-h-[85vh] flex flex-col p-0 gap-0">
        <DialogHeader className="flex-none px-5 pb-3 pt-5">
          <DialogTitle>{layer?.name ?? t.editableLayerTableDialog.attributeTableFallback}</DialogTitle>
          <DialogDescription>
            {loading
              ? t.editableLayerTableDialog.loading
              : t.editableLayerTableDialog.featureCount(formatNumber(features.length, lang), features.length) +
                (columns.length ? t.editableLayerTableDialog.propertyCount(columns.length) : "") +
                (changedRows ? t.editableLayerTableDialog.rowsEditedCount(changedRows) : "")}
          </DialogDescription>
        </DialogHeader>

        {error && (
          <div className="flex-none px-5 pb-3">
            <Banner tone="danger">{error}</Banner>
          </div>
        )}

        <div ref={scrollRef} className="thin-scroll min-h-0 flex-1 overflow-auto border-t border-line px-5">
          {loading ? (
            <div className="flex items-center gap-2 py-8 text-xs text-ink-muted">
              <Spinner className="h-3.5 w-3.5" /> {t.editableLayerTableDialog.loadingAttributes}
            </div>
          ) : features.length === 0 ? (
            <p className="py-8 text-xs text-ink-muted">{t.editableLayerTableDialog.noFeatures}</p>
          ) : columns.length === 0 ? (
            <p className="py-8 text-xs text-ink-muted">
              {t.editableLayerTableDialog.noProperties(formatNumber(features.length, lang))}
            </p>
          ) : (
            <table className="w-full min-w-max border-collapse text-left text-2xs">
              <thead className="sticky top-0 z-10 bg-surface">
                <tr>
                  <th className="whitespace-nowrap border-b border-line px-2 py-1.5 font-semibold text-ink-muted">
                    #
                  </th>
                  {columns.map((col) => (
                    <th
                      key={col}
                      className="whitespace-nowrap border-b border-line px-2 py-1.5 font-semibold text-ink-muted"
                    >
                      {col}
                    </th>
                  ))}
                  {/* Pinned to the right edge so it stays reachable on a layer
                      with thirty-odd attribute columns, where the row's own
                      Save would otherwise be several screens of horizontal
                      scrolling away from the cell just typed into. */}
                  {editMode && (
                    <th className="sticky right-0 z-10 whitespace-nowrap border-b border-l border-line bg-surface px-2 py-1.5 text-right font-semibold text-ink-muted">
                      {t.editableLayerTableDialog.saveRowHeader}
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {pageRows.map((i) => (
                  <tr key={i} className="odd:bg-surface-sunken/40">
                    <td className="whitespace-nowrap border-b border-line/60 px-2 py-1 text-ink-muted">
                      {i + 1}
                    </td>
                    {columns.map((col) =>
                      editMode ? (
                        <td key={col} className="border-b border-line/60 px-1 py-0.5">
                          <input
                            value={cellValue(i, col)}
                            onChange={(e) => setCell(i, col, e.target.value)}
                            disabled={saving}
                            aria-label={t.editableLayerTableDialog.cellAriaLabel(col, i + 1)}
                            className={
                              "w-full min-w-[7rem] rounded border bg-transparent px-1.5 py-1 text-2xs " +
                              "text-ink outline-none transition focus:border-accent focus:bg-surface " +
                              (isEdited(i, col)
                                ? "border-accent bg-accent-subtle/40"
                                : "border-transparent hover:border-line-strong")
                            }
                          />
                        </td>
                      ) : (
                        <td
                          key={col}
                          className="max-w-[14rem] truncate border-b border-line/60 px-2 py-1 text-ink"
                        >
                          {originalText(i, col) || "—"}
                        </td>
                      )
                    )}
                    {editMode && (
                      <td className="sticky right-0 z-10 border-b border-l border-line/60 bg-surface px-2 py-0.5 text-right">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => void saveRow(i)}
                          // Only this polygon's own pending edits enable it, so
                          // the button says something true about this row
                          // rather than about the table.
                          disabled={!edits[i] || saving || savingRow !== null}
                          title={
                            edits[i]
                              ? t.editableLayerTableDialog.saveRowTitle(i + 1)
                              : t.editableLayerTableDialog.nothingEditedRow
                          }
                        >
                          {savingRow === i ? (
                            <Spinner className="h-3 w-3" />
                          ) : (
                            <Save className="h-3.5 w-3.5" />
                          )}
                        </Button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {!loading && features.length > pageSize && (
          <div className="flex flex-none flex-wrap items-center justify-between gap-x-4 gap-y-1 border-t border-line px-5 py-2">
            <span className="text-2xs tabular-nums text-ink-muted">
              {t.editableLayerTableDialog.rowRange(
                formatNumber(firstRow + 1, lang),
                formatNumber(Math.min(firstRow + pageSize, features.length), lang),
                formatNumber(features.length, lang)
              )}
            </span>
            <div className="flex items-center gap-0.5">
              {(
                [
                  ["«", 1, t.editableLayerTableDialog.firstPage],
                  ["‹", currentPage - 1, t.editableLayerTableDialog.previousPage],
                ] as const
              ).map(([label, target, title]) => (
                <button
                  key={title}
                  type="button"
                  onClick={() => setPage(target)}
                  disabled={currentPage === 1}
                  title={title}
                  className="flex h-6 w-6 items-center justify-center rounded text-xs text-ink-muted transition hover:bg-surface-sunken hover:text-ink disabled:cursor-not-allowed disabled:opacity-40"
                >
                  {label}
                </button>
              ))}
              <span className="min-w-[5rem] text-center text-2xs tabular-nums text-ink-muted">
                {formatNumber(currentPage, lang)} / {formatNumber(pageCount, lang)}
              </span>
              {(
                [
                  ["›", currentPage + 1, t.editableLayerTableDialog.nextPage],
                  ["»", pageCount, t.editableLayerTableDialog.lastPage],
                ] as const
              ).map(([label, target, title]) => (
                <button
                  key={title}
                  type="button"
                  onClick={() => setPage(target)}
                  disabled={currentPage === pageCount}
                  title={title}
                  className="flex h-6 w-6 items-center justify-center rounded text-xs text-ink-muted transition hover:bg-surface-sunken hover:text-ink disabled:cursor-not-allowed disabled:opacity-40"
                >
                  {label}
                </button>
              ))}
            </div>
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(1);
              }}
              className="h-6 rounded border border-line bg-surface-sunken px-1.5 text-2xs text-ink focus:outline-none focus:ring-1 focus:ring-accent-solid"
              aria-label={t.editableLayerTableDialog.rowsPerPage}
            >
              {[50, 100, 250].map((n) => (
                <option key={n} value={n}>
                  {t.editableLayerTableDialog.perPage(n)}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Outside the scroll area: Save has to be reachable without scrolling
            past every row of a 100-feature layer to get to it. */}
        <div className="flex flex-none items-center justify-end gap-2 border-t border-line px-5 py-3">
          {editMode ? (
            <>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setEdits({})}
                disabled={changedRows === 0 || saving || savingRow !== null}
              >
                <Undo2 className="mr-1.5 h-3.5 w-3.5" />
                {t.editableLayerTableDialog.discard}
              </Button>
              <Button
                size="sm"
                onClick={() => {
                  selfSaveRef.current = true;
                  // Caught, not just voided: onSave rethrows so the per-row
                  // handler can keep its row pending, and an unhandled
                  // rejection here would be that same refusal escaping as a
                  // console error on a path that has already shown a banner.
                  void onSave(edits, true).catch(() => {
                    selfSaveRef.current = false;
                  });
                }}
                disabled={changedRows === 0 || saving || savingRow !== null}
              >
                {saving ? (
                  <Spinner className="mr-1.5 h-3 w-3" />
                ) : (
                  <Save className="mr-1.5 h-3.5 w-3.5" />
                )}
                {/* Named against the per-row button beside it: "Save changes"
                    read as the only save once rows had one of their own. */}
                {saving
                  ? t.editableLayerTableDialog.saving
                  : t.editableLayerTableDialog.saveAllRows(changedRows)}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setEditMode(false)}
                // Same reasoning as FeatureTable's Done: leaving edit mode
                // with cells still pending would either hide them or discard
                // them with no confirmation, so this waits for Save/Discard.
                disabled={changedRows > 0 || saving || savingRow !== null}
                title={changedRows > 0 ? t.editableLayerTableDialog.saveOrDiscardFirst : undefined}
              >
                <X className="mr-1.5 h-3.5 w-3.5" />
                {t.editableLayerTableDialog.done}
              </Button>
            </>
          ) : (
            <Button
              size="sm"
              variant="secondary"
              onClick={() => setEditMode(true)}
              disabled={loading || features.length === 0 || columns.length === 0}
            >
              <Pencil className="mr-1.5 h-3.5 w-3.5" />
              {t.editableLayerTableDialog.edit}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
